import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import AnimatedLoginBackground from '../components/AnimatedLoginBackground';
import { FormField, Button, Alert, Card } from '../ui';
import { PasswordField } from '../ui/forms';
import FormProvider from '../components/FormProvider';
import { loginSchema } from '../utils/validation';
import { sanitizeEmail } from '../utils/sanitize';

const Login = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const onSubmit = async (data) => {
    setError('');
    setLoading(true);

    // Data is already sanitized by FormField component
    const sanitizedEmail = sanitizeEmail(data.email);
    const result = await login(sanitizedEmail, data.password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 sm:py-12 relative">
      {/* Animated Background */}
      <AnimatedLoginBackground />
      
      {/* Login Form */}
      <div className="max-w-md w-full relative z-10">
        <Card variant="elevated" className="animate-fade-in p-8 sm:p-10 backdrop-blur-sm bg-white/90 dark:bg-primary-800/90 shadow-2xl border border-primary-200/50 dark:border-primary-700/50">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-8">
              <Logo className="h-12 sm:h-14 w-auto" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-primary-900 dark:text-primary-50 mb-2">
              Welcome Back
            </h1>
            <p className="text-primary-600 dark:text-primary-400 text-base">
              Sign in to your account
            </p>
          </div>

          {error && (
            <Alert variant="error" className="mb-6">
              {error}
            </Alert>
          )}

          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                label="Email"
                name="email"
                type="email"
                sanitizeType="email"
                required
                placeholder="you@example.com"
              />

              <PasswordField
                label="Password"
                name="password"
                required
                placeholder="••••••••"
              />

            <div className="flex items-center justify-between pt-1">
              <Link
                to="/forgot-password"
                className="text-sm text-secondary-600 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:underline transition-colors font-medium"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              loading={loading}
              variant="primary"
              size="lg"
              className="w-full mt-6"
            >
              Sign In
            </Button>
            </form>
          </FormProvider>

        </Card>
      </div>
    </div>
  );
};

export default Login;

